// Оптимизированные утилиты для измерения текста

// Кэш для измерений текста
const textMeasurementCache = new Map();
const maxCacheSize = 1000;

// Reusable canvas для измерений
let measureCanvas = null;
let measureContext = null;

// Web Worker статус
let workerStatus = {
    supported: typeof Worker !== 'undefined',
    enabled: false,
    active: false,
    pendingTasks: 0
};

function getCanvasContext() {
    if (!measureCanvas) {
        measureCanvas = document.createElement('canvas');
        measureContext = measureCanvas.getContext('2d');
    }
    return measureContext;
}

// Генерируем ключ для кэша
function getCacheKey(text, font, maxWidth) {
    return `${text}|${font}|${maxWidth || 'auto'}`;
}

// Очистка кэша при превышении лимита
function cleanCache() {
    if (textMeasurementCache.size > maxCacheSize) {
        const keysToDelete = Array.from(textMeasurementCache.keys()).slice(0, 100);
        keysToDelete.forEach(key => textMeasurementCache.delete(key));
    }
}

// Основная функция для измерения текста с кэшированием
export const measureTextOptimized = (text, size, maxWidth = null) => {
    const font = `${size}px Arial, sans-serif`;
    const cacheKey = getCacheKey(text, font, maxWidth);
    
    // Проверяем кэш
    if (textMeasurementCache.has(cacheKey)) {
        return textMeasurementCache.get(cacheKey);
    }
    
    let width, height;
    
    if (maxWidth) {
        // Для текста с ограничением ширины используем DOM измерения
        const temp = document.createElement('span');
        temp.style.font = font;
        temp.style.display = 'block';
        temp.style.maxWidth = `${maxWidth}px`;
        temp.style.width = `${maxWidth}px`;
        temp.style.lineHeight = '1';
        temp.style.position = 'absolute';
        temp.style.visibility = 'hidden';
        temp.innerHTML = text;
        
        document.body.appendChild(temp);
        const rect = temp.getBoundingClientRect();
        width = rect.width;
        height = rect.height;
        document.body.removeChild(temp);
    } else {
        // Для простого текста используем canvas
        const context = getCanvasContext();
        context.font = font;
        const metrics = context.measureText(text);
        width = metrics.width;
        height = size; // Приблизительная высота
    }
    
    const result = { width, height };
    
    // Сохраняем в кэш
    textMeasurementCache.set(cacheKey, result);
    cleanCache();
    
    return result;
};

// Alias для обратной совместимости
export const measureTextCached = measureTextOptimized;

// Batch измерения для массива текстов
export const measureTextBatch = (textItems, size, maxWidth = null) => {
    return textItems.map(item => {
        if (typeof item === 'string') {
            return measureTextOptimized(item, size, maxWidth);
        } else {
            // Если передан объект с текстом и параметрами
            return measureTextOptimized(
                item.text, 
                item.size || size, 
                item.maxWidth || maxWidth
            );
        }
    });
};

// Alias для обратной совместимости
export const measureTextsBatch = measureTextBatch;

// Асинхронная версия для больших массивов
export const measureTextBatchAsync = async (textItems, size, maxWidth = null, batchSize = 50) => {
    const results = [];
    
    for (let i = 0; i < textItems.length; i += batchSize) {
        const batch = textItems.slice(i, i + batchSize);
        const batchResults = measureTextBatch(batch, size, maxWidth);
        results.push(...batchResults);
        
        // Даем браузеру возможность обработать другие задачи
        if (i + batchSize < textItems.length) {
            await new Promise(resolve => setTimeout(resolve, 0));
        }
    }
    
    return results;
};

// Alias для обратной совместимости
export const measureTextsBatchAsync = measureTextBatchAsync;

// Очистка кэша (для использования при изменении размера окна)
export const clearTextMeasurementCache = () => {
    textMeasurementCache.clear();
};

// Alias для обратной совместимости
export const clearTextSizeCache = clearTextMeasurementCache;

// Предварительная загрузка шрифта
export const preloadFont = () => {
    const testText = 'Ag';
    measureTextOptimized(testText, 12);
    measureTextOptimized(testText, 10);
    measureTextOptimized(testText, 14);
};

// Получение статистики кэша
export const getCacheStats = () => {
    const keys = Array.from(textMeasurementCache.keys()).slice(-10); // Последние 10 ключей
    return {
        size: textMeasurementCache.size,
        maxSize: maxCacheSize,
        keys: keys
    };
};

// Web Worker функции (заглушки для совместимости)
export const enableWebWorker = () => {
    workerStatus.enabled = true;
    console.log('Web Worker enabled (mock)');
};

export const disableWebWorker = () => {
    workerStatus.enabled = false;
    workerStatus.active = false;
    console.log('Web Worker disabled');
};

export const getWorkerStatus = () => {
    return { ...workerStatus };
};

// Настройка обработчика изменения размера окна
export const setupWindowResizeHandler = () => {
    const handleResize = () => {
        clearTextMeasurementCache();
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
        window.removeEventListener('resize', handleResize);
    };
}; 